import { ref } from 'vue';
import { z } from 'zod';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';

const serviceListDetailInputSchema = z
  .object({
    serviceId: z.string().optional(),
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    targetKind: z.string().optional(),
    rawData: z.record(z.any()).optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
  })
  .transform((input) => ({
    ...input,
    serviceId: input.serviceId || input.rawData?.serviceId || input.targetId || '',
    targetKind: input.targetKind || TargetStatusRequestTargetKind.BUSINESS_SERVICE,
  }))
  .pipe(
    z.object({
      serviceId: z.string().min(1),
      targetName: z.string().optional(),
      targetKind: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type ServiceListDetailInput = z.input<typeof serviceListDetailInputSchema>;

export const serviceDetailRequiredMap = getRequiredMap(serviceListDetailInputSchema);

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

const serviceTargetKindMap: Record<string, TargetStatusRequestTargetKind> = {
  business_service: TargetStatusRequestTargetKind.BUSINESS_SERVICE,
  sms: TargetStatusRequestTargetKind.SERVICE,
};

const getServiceTargetKind = (targetKind?: string): TargetStatusRequestTargetKind => {
  if (!targetKind) return TargetStatusRequestTargetKind.BUSINESS_SERVICE;
  return (
    serviceTargetKindMap[targetKind.toLowerCase()] ?? TargetStatusRequestTargetKind.BUSINESS_SERVICE
  );
};

export const useServiceListAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const validateServiceListDetail = async (
    params: Partial<ServiceListDetailInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.output<typeof serviceListDetailInputSchema>;
  }> => {
    const { checkPermission = true, checkTarget = true } = options;

    // 0. 입력 파싱 및 필수값 검증
    const inputParsed = serviceListDetailInputSchema.safeParse(params);
    if (!inputParsed.success) {
      const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
        value: getMissingParams(inputParsed.error) || 'serviceId',
      });
      if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
      return { reason: 'invalid_params' };
    }
    const normalizedParams = inputParsed.data;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        type: TOTAL_DETAIL.SERVICE_LIST,
        rolePermissionKey:
          rolePermissionKey || ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS_LIST,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: normalizedParams.serviceId,
          targetKind: getServiceTargetKind(normalizedParams.targetKind),
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: options.showErrorMessage,
        },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    return { reason: 'ok', parsedParams: normalizedParams };
  };

  const openServiceListDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<
    Partial<ServiceListDetailInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      runTargetValidation = true,
      showErrorMessage = true,
    } = options ?? {};

    const { reason, parsedParams } = await validateServiceListDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    // 4. 탭 패널 열기
    const result = addTabPanel(
      {
        type: TOTAL_DETAIL.SERVICE_LIST,
        name: validResult.targetName ?? '',
        addition: {
          serviceId: validResult.serviceId,
          fromTime: validResult.fromTime,
          toTime: validResult.toTime,
        },
        init,
        state: { ...state, selectedTab },
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openServiceListDetail };
};
